import {
	useGetAllLocalizationCountriesQuery,
	useUpdateManufacturerMutation,
} from '@codezo/graphql';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { VetterSelector } from '../../components/selectors/VetterSelector';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectPopover } from '../../components/ui/select-popover';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';

/* ── Types ── */

interface ManufacturerData {
	id: string;
	name: string;
	country?: string | null;
	description?: string | null;
	website?: string | null;
	isActive: boolean;
	vetterEntityId?: string | null;
}

/* ════════════════════════ EDIT MANUFACTURER MODAL ════════════════════════ */

export function EditManufacturerModal({
	open,
	onOpenChange,
	manufacturer,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	manufacturer: ManufacturerData | null;
	onSuccess?: () => void;
}) {
	const [name, setName] = useState('');
	const [country, setCountry] = useState('');
	const [description, setDescription] = useState('');
	const [website, setWebsite] = useState('');
	const [vetterEntityId, setVetterEntityId] = useState('');
	const [isActive, setIsActive] = useState(true);

	const [updateManufacturer, { loading }] = useUpdateManufacturerMutation();

	const { data: countriesData } = useGetAllLocalizationCountriesQuery({
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const countryOptions = useMemo(
		() =>
			countriesData?.getAllLocalizationCountries?.map((c) => ({
				value: c.name,
				label: c.name,
			})) ?? [],
		[countriesData]
	);

	useEffect(() => {
		if (open && manufacturer) {
			setName(manufacturer.name);
			setCountry(manufacturer.country || '');
			setDescription(manufacturer.description || '');
			setWebsite(manufacturer.website || '');
			setVetterEntityId(manufacturer.vetterEntityId || '');
			setIsActive(manufacturer.isActive);
		}
	}, [open, manufacturer]);

	const handleSubmit = async () => {
		if (!manufacturer || !name.trim()) return;

		try {
			const { data } = await updateManufacturer({
				variables: {
					input: {
						id: manufacturer.id,
						name: name.trim(),
						country: country || undefined,
						description: description.trim() || undefined,
						website: website.trim() || undefined,
						// Explicit null (not undefined) so clearing the selector actually
						// unassigns the vetter — Apollo strips undefined from variables.
						vetterEntityId: vetterEntityId || null,
						isActive,
					},
				},
			});

			if (data?.updateManufacturer?.success) {
				toast.success('Manufacturer updated successfully');
				onOpenChange(false);
				onSuccess?.();
			} else {
				toast.error(data?.updateManufacturer?.message || 'Failed to update manufacturer');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update manufacturer');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit manufacturer</DialogTitle>
					<DialogDescription>Update manufacturer details.</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-mfr-name">
									Name <span className="text-destructive">*</span>
								</Label>
								<Input
									id="edit-mfr-name"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Shenzhen Hexin"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Country</Label>
								<SelectPopover
									value={country}
									onChange={setCountry}
									options={countryOptions}
									placeholder="Select country"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-mfr-description">Description</Label>
								<Textarea
									id="edit-mfr-description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="What does this manufacturer produce?"
									rows={3}
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-mfr-website">Website</Label>
								<Input
									id="edit-mfr-website"
									value={website}
									onChange={(e) => setWebsite(e.target.value)}
									placeholder="https://example.com"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Vetter entity</Label>
								<VetterSelector
									value={vetterEntityId}
									onChange={setVetterEntityId}
									placeholder="Assign a vetter (optional)"
									clearable
									creatable
								/>
							</div>

							<div className="flex items-center justify-between rounded-lg border border-line-2 px-3 py-2.5">
								<div>
									<Label htmlFor="edit-mfr-active" className="text-sm">
										Active
									</Label>
									<p className="text-sm text-muted-foreground">
										Mark this manufacturer as active or inactive
									</p>
								</div>
								<Switch
									id="edit-mfr-active"
									checked={isActive}
									onCheckedChange={setIsActive}
								/>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || loading}>
							{loading ? 'Saving…' : 'Save changes'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
